import type { PanelPlugin, PluginContext } from '../core/plugin'
import type { MessageBridge } from './message-bridge'
import { CONFIG } from './config'
import { ICONS } from './icons'
import { log } from './utils'

export class GeneralAIPanel implements PanelPlugin {
  readonly name = 'ai-panel'

  private bridge: MessageBridge

  private panel: HTMLDivElement | null = null

  private activeElement: HTMLElement | null = null

  private isVisible = false

  private _scrollHandler: (() => void) | null = null

  constructor(messageBridge: MessageBridge) {
    this.bridge = messageBridge
  }

  get visible(): boolean {
    return this.isVisible
  }

  create(_ctx: PluginContext): void {
    this._createPanel()
  }

  private _createPanel(): void {
    const panel = document.createElement('div')
    panel.className = 'musk-ai-panel'
    panel.setAttribute(CONFIG.TOOLBAR_ATTR, 'true')
    panel.innerHTML = [
      '<div class="musk-ai-widget">',
      '  <div class="musk-ai-row">',
      `    <span class="musk-ai-label">${ICONS.AI_SPARKLE}</span>`,
      '    <input type="text" class="musk-ai-input" placeholder="描述你的修改需求...">',
      `    <button class="musk-ai-send" disabled>${ICONS.SEND_ARROW}</button>`,
      '  </div>',
      '  <div class="musk-ai-preview"></div>',
      '  <div class="musk-ai-preview-actions">',
      '    <button class="musk-ai-discard">放弃</button>',
      `    <button class="musk-ai-regenerate">${ICONS.REGENERATE_SMALL}重新生成</button>`,
      '    <button class="musk-ai-apply">应用</button>',
      '  </div>',
      '</div>',
    ].join('\n')
    document.body.appendChild(panel)
    this.panel = panel
    this._bindEvents()
    this._setupScrollHandler()
  }

  private _bindEvents(): void {
    const input = this.panel!.querySelector<HTMLInputElement>('.musk-ai-input')!
    const sendBtn = this.panel!.querySelector<HTMLButtonElement>('.musk-ai-send')!

    const doSend = (): void => {
      const text = input.value.trim()
      if (!text || !this.activeElement) return
      const elementId = this.activeElement.getAttribute('data-musk-element-id') || ''
      this.bridge.send('AI_EDIT_REQUEST', {
        mode: 'general',
        elementId,
        content: this.activeElement.outerHTML,
        innerText: (this.activeElement.innerText || '').substring(0, 2000),
        prompt: text,
      })
      input.value = ''
      sendBtn.disabled = true
      log('AI 编辑请求已发送', { mode: 'general', elementId })
    }

    this.panel!.addEventListener('mousedown', (e: MouseEvent) => {
      e.stopPropagation()
    }, true)

    this.panel!.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation()
      const t = e.target as HTMLElement
      if (t.closest?.('.musk-ai-send')) {
        e.preventDefault()
        doSend()
      }
    }, true)

    input.addEventListener('keydown', (e: KeyboardEvent) => {
      e.stopPropagation()
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doSend() }
      if (e.key === 'Escape') { this.hide() }
    })
    input.addEventListener('input', () => {
      sendBtn.disabled = !input.value.trim()
    })
  }

  private _setupScrollHandler(): void {
    this._scrollHandler = () => {
      if (this.isVisible && this.activeElement) this._updatePosition()
    }
    window.addEventListener('scroll', this._scrollHandler, { passive: true })
  }

  show(element: HTMLElement, _ctx: PluginContext): void {
    if (!this.panel) this._createPanel()
    if (this.activeElement && this.activeElement !== element) {
      this.activeElement.classList.remove('musk-general-editing')
    }
    this.activeElement = element
    element.classList.add('musk-general-editing')
    element.classList.remove('musk-general-hover')
    this.panel!.style.display = 'block'
    this.isVisible = true
    this._updatePosition()
    const input = this.panel!.querySelector<HTMLInputElement>('.musk-ai-input')
    const sendBtn = this.panel!.querySelector<HTMLButtonElement>('.musk-ai-send')
    if (input) { input.value = ''; setTimeout(() => { input.focus() }, 50) }
    if (sendBtn) sendBtn.disabled = true
  }

  hide(): void {
    if (!this.panel) return
    this.panel.style.display = 'none'
    this.isVisible = false
    if (this.activeElement) {
      this.activeElement.classList.remove('musk-general-editing')
      this.activeElement = null
    }
  }

  private _updatePosition(): void {
    if (!this.activeElement || !this.panel) return
    const rect = this.activeElement.getBoundingClientRect()
    const panelH = this.panel.offsetHeight
    const panelW = this.panel.offsetWidth
    const gap = 8
    let top = rect.bottom + gap
    if (top + panelH > window.innerHeight - 10) top = rect.top - panelH - gap
    if (top < 10) top = 10
    let left = rect.left + (rect.width - panelW) / 2
    if (left + panelW > window.innerWidth - 10) left = window.innerWidth - panelW - 10
    if (left < 10) left = 10
    this.panel.style.top = `${top}px`
    this.panel.style.left = `${left}px`
  }

  destroy(): void {
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler)
    }
    if (this.panel) { this.panel.remove(); this.panel = null }
    this.activeElement = null
    this.isVisible = false
  }
}
